<script setup lang="ts">
import { computed, watch } from "vue";
import { isNullish } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation";
import Capitalize from "@Resources/js/filters/Capitalize";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import OrgSettingsContent from "@Modules/teams/components/OrgSettingsContent.vue";
import IconProfileForm from "@Modules/shared/Components/IconProfileForm.vue";
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts";
import type { AuthUser } from "@Modules/users/Store/XAuthStore";
import UserProfileForm from "@Modules/users/components/UserProfileForm.vue";
import { logWarning } from "@Resources/js/helpers/logger";

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
const props = defineProps<{
  user: AuthUser
  organization: Organization
  errors: Record<string, string>
  loading: { contact: boolean; photo: boolean }
}>();

//
// Emit
interface ContactPayload {
  name: string;
  email: string;
}

const emit = defineEmits<{
  (e: "update-contact", payload: ContactPayload): void
  (e: "update-icon", picture: File): void
}>();

//
// Computed
const membership = computed(() => props.organization.defaultTeam()?.membership ?? null);

const roleKey = computed<string>(() => {
  const role = membership.value?.role;

  return role ? role.toLowerCase() : "";
});

const roleName = computed<string>(() => (roleKey.value ? Capitalize(roleKey.value) : ""));

const roleDescription = computed<string>(() => {
  if (!roleKey.value) {
    return "";
  }

  const key = `organization_user_profile_settings.role_permission.descriptions.${roleKey.value}`;
  const description = __(key);

  return description === key ? "" : description;
});

watch(() => membership, (value) => {
  if (isNullish(value)) {
    logWarning("[OrgUserProfileSettings]: Membership couldn't be loaded!");
  }
});
</script>

<template>
  <div
    class="org-user-profile-settings"
    :class="{ 'org-user-profile-settings--mobile': isMobileView }"
  >
    <SettingsTopBar :title="__('organization_user_profile_settings.title')" />

    <OrgSettingsContent :title="__('organization_user_profile_settings.icon_section_title')">
      <IconProfileForm
        :user-name="user.name"
        :profile-src="user.photo_url"
        :errors="errors"
        :loading="loading.photo"
        @submit="(picture) => emit('update-icon', picture)"
      />
    </OrgSettingsContent>

    <OrgSettingsContent :title="__('organization_user_profile_settings.contact_info_section_title')">
      <UserProfileForm
        :name="user.name"
        :email="user.email"
        :errors="errors"
        :loading="loading.contact"
        @submit="(payload) => emit('update-contact', payload)"
      />
    </OrgSettingsContent>

    <OrgSettingsContent
      v-if="roleName"
      :title="__('organization_user_profile_settings.role_permission_section_title')"
    >
      <div class="org-user-profile-settings__role">
        <p class="org-user-profile-settings__role-name _role_name">
          {{ roleName }}:
        </p>
        <p
          v-if="roleDescription"
          class="org-user-profile-settings__role-description _role_description"
        >
          {{ roleDescription }}
        </p>
      </div>
    </OrgSettingsContent>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.org-user-profile-settings {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$s;

  &__role {
    display: grid;
    grid-template-columns: 1fr 3fr;
    padding: spacing.$xxs;

    background-color: color.$background-secondary;
    border-radius: border.$medium-radius;
    border: border.$primary;
  }

  &__role-name,
  &__role-description {
    margin: 0;
  }

  &__role-name {
    font-weight: typography.$font-weight-semi-bold;
  }

  &--mobile {
    #{ $self }__role {
      display: flex;
      flex-flow: column;
      gap: spacing.$xxxs;
    }
  }
}
</style>
